import * as React from 'react';

import Box from '@mui/material/Box';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import {
  GridRowSelectionModel
} from '@mui/x-data-grid';
import { Grid } from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Tooltip from '@mui/material/Tooltip';
import { useSnackbar } from 'notistack';

// Styles
import { useTheme } from '@mui/material/styles';

// Components
import PassesByDateGrid from './PassesByDateGrid'
import PassesListGrid from './PassesListGrid'
import PassesMobile from './PassesMobile'
import PassesToolbar from './PassesToolbar'

import {
  HTTPHeaders
} from "../../interfaces";

import {
  DateRange,
  RequestPass,
  TransferablePass,
  TransferablePassStatusChange
} from '../../models';

import {
  API_URL,
  PassesViewMode,
  TransferablePassAction
} from '../../global';

// Auth0 user hooks 
import { useAuth0User } from '../../hooks/UseAuth0User';

// Responsive hooks
import { useResponsive } from '../../hooks/UseResponsive';

interface PassesProps {
  dateRange: DateRange;
  days: number;
}

const Passes: React.FC<PassesProps> = (props) => {

  const { enqueueSnackbar } = useSnackbar();
  const { getAccessToken, isPassAdmin } = useAuth0User();
  const { isMobile } = useResponsive();

  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [viewMode, setViewMode] = React.useState<PassesViewMode>(PassesViewMode.ByDate);
  const [passesByDate, setPassesByDate] = React.useState<Array<Array<RequestPass>>>([]);
  const [passesList, setPassesList] = React.useState<Array<TransferablePass>>([]);
  const [passesSelected, setPassesSelected] = React.useState<{
    type: string;
    ids: Set<number>; // Tell TS this is a Set of numbers
  }>({
    type: 'include',
    ids: new Set<number>()
  });

  // #region Styles

  const theme = useTheme();
  const styles = {
    gridContainer: {
      flexGrow: 1,
      height: 440,
      padding: theme.spacing(0, 1, 0, 1),
    },
    passesViewOptions: {
      padding: theme.spacing(0, 0, 1, 0),
    },
    toolbarContaner: {
      padding: theme.spacing(0, 0, 0, 1),
    }
  };

  // #endregion

  // #region Methods

  /*
  * Clear passes data
  */
  const clearPasses = () => {
    setPassesByDate([]);
    setPassesList([]);
    setPassesSelected({
      type: 'include',
      ids: new Set()
    });
}

  /*
 * Fetch request passes by date
 */
  const fetchPassesByDate = async () => {

    setIsLoading(true);

    // Auth0 API token
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {};
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;

    // Always show current date forward
    const params = new URLSearchParams();
    if (props.dateRange !== undefined) {
      if (props.dateRange.startDate) {
        params.append('startDate', props.dateRange.startDate);
      }
      if (props.dateRange.endDate) {
        params.append('endDate', props.dateRange.endDate);
      }
    }

    const url = new URL(`${API_URL}/v1/requests/requestpasses`);
    url.search = params.toString();

    await fetch(url.toString(), {
      method: 'GET',
      cache: 'no-cache',
      headers: headers,
      mode: 'cors'
    })
      .then((response) => {
        if (!response.ok) {
          setIsLoading(false);
          throw new Error('Request passes not retrieved');
        }
        return response.json()
      })
      .then((gridData: Array<Array<RequestPass>>) => {
        setPassesByDate(gridData);
        setIsLoading(false);
      })
      .catch(err => {
        setIsLoading(false);
      });
  }

  /*
* Fetch request passes as a list
*/
  const fetchPassesList = async () => {

    setIsLoading(true);

    // Auth0 API token
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {};
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;

    // Always display passes today+
    const params = new URLSearchParams();
    params.append('startDate', props.dateRange.startDate);
    params.append('endDate', props.dateRange.endDate);

    if (!params.toString()) {
      return;
    }

    const url = new URL(`${API_URL}/v1/passes`);
    url.search = params.toString();

    await fetch(url.toString(),
      {
        method: 'GET',
        cache: 'no-cache',
        headers: headers,
        mode: 'cors'
      })
      .then((response) => {
        if (!response.ok) {
          setIsLoading(false);
          throw new Error('Transferable passes not retrieved');
        }
        return response.json()
      })
      .then((e: Array<TransferablePass>) => {
        setPassesList(e);
        //setPassesSelected([]);
        setIsLoading(false);
      })
      .catch(err => {
        setIsLoading(false);
        throw new Error('Transferable passes not retrieved');
      });
  }

  /*
 * Change transferable pass status
 */
  const updateStatus = async (action: TransferablePassAction) => {
    // 1. Process selected passes
    const statusChange = new TransferablePassStatusChange();

    // 2. Extract and convert IDs safely
    // Array.from(set, mapFn) is cleaner than spreading when you need to cast types
    const ids: number[] = Array.from(passesSelected.ids).map((id) => Number(id));

    statusChange.ids = ids;
    statusChange.statusID = action;

    try {
      // 3. Auth0 API token
      const accessToken = await getAccessToken();

      const headers: HTTPHeaders = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': `Bearer ${accessToken}`,
      };

      const url = new URL(`${API_URL}/v1/passes/updatestatus`);

      const response = await fetch(url.toString(), {
        body: JSON.stringify(statusChange),
        headers: headers,
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error('Transferable pass status update error');
      }

      // 4. Refresh data and notify user
      fetchPassesList();
      fetchPassesByDate();
      enqueueSnackbar('Status updated for selected transferable passes', { variant: 'success' });

    } catch (err) {
      // You can log the actual error here for debugging: console.error(err);
      enqueueSnackbar('Failed to update status', { variant: 'error' });
      throw new Error('Transferable pass status update error');
    }
  };

  // #endregion

  // #region Event handlers

  /*
  * Handle when a pass request is selected
  */
  const handlePassessSelected = (selectedRows: GridRowSelectionModel) => {
    // We must transform the Set<GridRowId> into a Set<number> 
    // to match your state definition.
    const idsSelected: { type: 'include' | 'exclude'; ids: Set<number> } = {
      type: selectedRows.type,
      ids: new Set(Array.from(selectedRows.ids).map(id => Number(id)))
    };

    setPassesSelected(idsSelected);
  };

  /*
 * Handle view mode change
 */
  const handleViewModeChanged = (event: React.MouseEvent<HTMLElement>, m: PassesViewMode) => {
    if (m !== null) {
      setViewMode(m);
    }
  };

  React.useEffect(() => {
    if (props.dateRange.startDate && props.dateRange.endDate) {
      fetchPassesByDate();
      if (isPassAdmin()) {
        fetchPassesList();
      }
    } else {
      clearPasses();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.dateRange.startDate, props.dateRange.endDate]);

  // #endregion

  return (
    <>
      {!isMobile &&
        <Box>
          {(isPassAdmin()) &&
            <Grid container spacing={2} alignItems="center" sx={styles.toolbarContaner}>
              <Grid>
                <ToggleButtonGroup
                  value={viewMode}
                  exclusive
                  onChange={handleViewModeChanged}
                  sx={styles.passesViewOptions}
                >
                  <ToggleButton value={PassesViewMode.ByDate}>
                    <Tooltip title="Passes by Date">
                      <CalendarMonthIcon />
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton value={PassesViewMode.List}>
                    <Tooltip title="Passes List">
                      <FormatListBulletedIcon />
                    </Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>
              </Grid>
              {viewMode === PassesViewMode.List &&
                <PassesToolbar
                  handleStatusChange={updateStatus}
                  passesSelected={passesSelected}
                />
              }
            </Grid>
          }
          <Box sx={styles.gridContainer}>
            {viewMode === PassesViewMode.ByDate &&
              <PassesByDateGrid
                dateRange={props.dateRange}
                days={props.days}
                isLoading={isLoading}
                rows={passesByDate}
              />
            }
            {(isPassAdmin()) && viewMode === PassesViewMode.List &&
              <PassesListGrid
                handlePassesSelected={handlePassessSelected}
                isLoading={isLoading}
                passesSelected={passesSelected}
                rows={passesList}
              />
            }
          </Box>
        </Box >
      }
      {
        isMobile &&
        <PassesMobile
          dateRange={props.dateRange}
          days={props.days}
          isLoading={isLoading}
          rows={passesByDate}
        />
      }
    </>
  );

}

export default Passes;